import { formatCoin, formatPrice } from '@polaris/core/utils'
import { AnimatePresence, motion } from 'motion/react'
import { memo, useCallback, useMemo } from 'react'
import { useTokenSelection } from '@/src/stores/token-selection/actions'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import {
  CopyAddressButton,
  type CopyAddressButtonProps,
} from '../../token-selection/copy-button'
import {
  ListRowButton,
  ListRowImage,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../../ui/list-row'
import { TooltipContent } from '../../ui/tooltip'

interface TokenItemProps {
  variant_logo_img_url: string
  size: number
  start: number
  onSelectToken:
    | ((chainId: string, denom: string, ticker: string) => void)
    | undefined
  chainId: string
  denom: string
  ticker: string
  variant_display_name: string
  direction: 'from' | 'to'
  denoms?: {
    denom: string
    chainName: string
  }[]
  amount?: number
  usdValue?: number
  chainImgUrl?: string
  chainName?: string
}

function TokenItem({
  onSelectToken,
  size,
  start,
  variant_logo_img_url,
  chainId,
  denom,
  ticker,
  variant_display_name,
  denoms = [],
  amount,
  usdValue,
  chainImgUrl,
  chainName,
}: TokenItemProps) {
  const { filteredNetwork } = useTokenSelection()
  const showAddress = denoms.length === 1 && !amount && !usdValue

  const onSelect = useCallback(
    () => onSelectToken?.(chainId, denom, ticker),
    [onSelectToken, chainId, denom, ticker],
  )

  const Comp = showAddress ? CopyAddressButton : 'div'

  const tooltipProps: CopyAddressButtonProps = useMemo(
    () =>
      showAddress
        ? {
            tooltipContent: ({ isHolding }) => (
              <TooltipContent
                side='right'
                hideArrow
                className='flex flex-col gap-1 max-w-64'
              >
                <AnimatePresence mode='popLayout'>
                  {!isHolding && (
                    <motion.span
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                      }}
                      className='text-xs text-primary font-semibold'
                    >
                      Token address
                    </motion.span>
                  )}
                </AnimatePresence>
                <AnimatePresence mode='popLayout'>
                  {isHolding && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                      }}
                      className='text-xs text-success-text font-semibold'
                    >
                      Copied to clipboard
                    </motion.span>
                  )}
                </AnimatePresence>
                <span className='text-xs text-secondary break-words'>
                  {denom}
                </span>
              </TooltipContent>
            ),
            contentToCopy: denom,
            triggerButtonClassName: 'top-5',
          }
        : {},
    [denom, showAddress],
  )

  /**
   * Show denoms only if there is no balance to that denom.
   *
   * Valid only in search as we are passing amount and value as undefined
   * if the user is not searching
   */
  const shouldShowDenoms = denoms.length > 0 && !filteredNetwork

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${size}px`,
        transform: `translateY(${start}px)`,
      }}
    >
      <Comp {...tooltipProps}>
        <ListRowButton
          onClick={onSelect}
          className='py-2 group-hover:bg-surface1 peer'
        >
          <div className='flex items-center gap-3'>
            <ListRowImage
              src={variant_logo_img_url}
              alt={`${ticker} icon`}
              width={40}
              height={40}
              className='rounded-full max-h-10'
              unoptimized
              placeholderName={ticker}
              chainId={chainId}
              classes={{
                container: 'w-10',
              }}
              chainImageSrc={chainImgUrl}
              chainImageAlt={chainName}
            />
            <ListRowSide>
              <ListRowSideName className='max-w-44 truncate'>
                {variant_display_name}
              </ListRowSideName>
              <ListRowSideDescription>
                <span className='text-secondary'>{ticker}</span>{' '}
                {shouldShowDenoms && (
                  <span className='group-hover:opacity-100 text-tertiary-100 opacity-0 transition-opacity ease-default'>
                    on {denoms.length > 1 ? denoms.length : denoms[0].chainName}{' '}
                    {denoms.length > 1 && 'networks'}
                  </span>
                )}
              </ListRowSideDescription>
            </ListRowSide>
          </div>
          {usdValue && amount && (
            <ListRowSide className='text-right'>
              <ListRowSideName className='font-normal'>
                {formatPrice(usdValue)}
              </ListRowSideName>
              <ListRowSideDescription>
                {formatCoin({
                  amount,
                })}
              </ListRowSideDescription>
            </ListRowSide>
          )}
        </ListRowButton>
      </Comp>
    </div>
  )
}

export default memo(TokenItem)
